import React, { useState, useEffect } from "react";
import {
  Button,
  Grid,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Formik, Form } from "formik";
import "date-fns";
import {
  getCurrentProfileTeacher,
  createOrUpdateProfileTeacher,
} from "../../../actions/profileTeacher";
import * as yup from "yup";
import { Link } from "react-router-dom";
import ProfileTeacherPage1 from "./ProfileTeacherPage1";
import ProfileTeacherPage2 from "./ProfileTeacherPage2";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { connect } from "react-redux";
import SaveIcon from "@mui/icons-material/Save";
import BackdropSpinner from "../../../components/ui/BackdropSpinner";
import { RootState } from "../../../reducers";

const useStyles = makeStyles((theme) => ({
  card: {
    // Provide some spacing between cards
    margin: 16,

    // Use flex layout with column direction for components in the card
    // (CardContent and CardActions)
    display: "flex",
    flexDirection: "column",

    // Justify the content so that CardContent will always be at the top of the card,
    // and CardActions will be at the bottom
    justifyContent: "space-between",
  },
  paddingContainer: {
    padding: "0 4em",
  },
  linkText: {
    textTransform: "uppercase",
    "&:hover, &:visited, &:active": {
      textTransform: "uppercase",
      color: "inherit",
    },
  },
  formControl: {
    marginBottom: "1em",
  },
  rowContainer: {
    padding: "0 4em",
  },
  degreeImageCard: {
    maxWidth: 300,
  },
  degreeImage: {
    width: "100%",
    height: "auto",
  },
  expImageCard: {
    maxWidth: 300,
  },
  expImage: {
    width: "100%",
    height: "auto",
  },
}));

interface Props {
  profileTeacher: any;
  history: any;
  getCurrentProfileTeacher: any;
  createOrUpdateProfileTeacher: any;
}

const ProfileTeacher: React.FC<Props> = ({
  profileTeacher: { loading, uploading, profileTeacher },
  history,
  getCurrentProfileTeacher,
  createOrUpdateProfileTeacher,
}) => {
  const [formData, setFormData] = useState<any>({
    hometown: "",
    degreeImages: [],
    selectedDegreeImages: [],
    expImages: [],
    selectedExpImages: [],
    lessons: [],
    phoneNumber: "",
    user: null,
    dateOfBirth: "",
    introduction: "",
    teacherAvatar: null,
    thumbnail: null,
    typeOfTeacher: "",
    video: null,
    selectedVideo: null,
    lesson: null,
    bankName: "",
    cardNumber: "",
    nameOnCard: "",
    skypeId: "",
  });

  const [page, setPage] = useState(0);
  const [activeStep, setActiveStep] = useState(0);

  const theme = useTheme();
  const matchesMD = useMediaQuery(theme.breakpoints.up("md"));

  const initialValues = {
    hometown: null,
    degreeImages: [],
    selectedDegreeImages: [],
    expImages: [],
    selectedExpImages: [],
    lessons: null,
    phoneNumber: "",
    user: null,
    dateOfBirth: null,
    introduction: "",
    teacherAvatar: "",
    selectedTeacherAvatar: "",
    thumbnail: "",
    typeOfTeacher: "",
    video: "",
    selectedVideo: "",
    lesson: "",
    nameOnCard: "",
    bankName: "",
    cardNumber: "",
    skypeId: "",
  };

  const isButtonDisabled = (values: any) => {
    const {
      typeOfTeacher,
      dateOfBirth,
      hometown,
      phoneNumber,
      introduction,
      skypeId,
      selectedTeacherAvatar,
      teacherAvatar,
      selectedVideo,
      video,
      degreeImages,
      selectedDegreeImages,
      expImages,
      selectedExpImages,
    } = values;

    if (values && typeOfTeacher === "commutor") {
      if (
        !dateOfBirth ||
        !hometown ||
        !phoneNumber ||
        !introduction ||
        !skypeId ||
        (!selectedVideo && !video) ||
        (!selectedTeacherAvatar && !teacherAvatar)
      ) {
        return true;
      }
    } else if (values && typeOfTeacher === "professional") {
      if (
        !dateOfBirth ||
        !hometown ||
        !phoneNumber ||
        !introduction ||
        !skypeId ||
        (!selectedVideo && !video) ||
        (!selectedTeacherAvatar && !teacherAvatar) ||
        (degreeImages.length === 0 && selectedDegreeImages.length === 0) ||
        (expImages.length === 0 && selectedExpImages.length === 0)
      ) {
        return true;
      }
    }
  };

  // cac step trong stepper
  const getSteps = () => {
    return ["Choose a type of teacher", "Fill your profile as a teacher"];
  };

  const steps = getSteps();

  const classes = useStyles();

  // buoc ke tiep cua stepper
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  // tro ve cua stepper
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Proceed to next page
  const nextPage = () => {
    setPage(page + 1);
    handleNext();
  };

  // Go back to prev page
  const prevPage = () => {
    setPage(page - 1);
    handleBack();
  };

  const pages = [
    <ProfileTeacherPage1 profileTeacher={profileTeacher} nextPage={nextPage} />,
    <ProfileTeacherPage2 />,
  ];

  const validationSchema = yup.object().shape({
    dateOfBirth: yup.date().nullable(),
    hometown: yup.object().nullable().required("Required"),
    phoneNumber: yup.number().required("Required"),
    introduction: yup.string().required("Required"),
    skypeId: yup.string().required("Required"),
    typeOfTeacher: yup.string().required("Required"),
  });

  useEffect(() => {
    if (!profileTeacher) getCurrentProfileTeacher();
    if (!loading && profileTeacher) {
      setFormData({
        hometown: profileTeacher.hometown,
        degreeImages: profileTeacher.degreeImages,
        selectedDegreeImages: [],
        expImages: profileTeacher.expImages,
        selectedExpImages: [],
        lessons: profileTeacher.lessons,
        phoneNumber: profileTeacher.phoneNumber,
        user: profileTeacher.user,
        dateOfBirth: profileTeacher.dateOfBirth,
        introduction: profileTeacher.introduction,
        teacherAvatar: profileTeacher.teacherAvatar,
        thumbnail: profileTeacher.thumbnail,
        typeOfTeacher: profileTeacher.typeOfTeacher,
        video: profileTeacher.video,
        selectedVideo: "",
        lesson: profileTeacher.lesson,
        bankName: profileTeacher.bankName,
        cardNumber: profileTeacher.cardNumber,
        nameOnCard: profileTeacher.nameOnCard,
        skypeId: profileTeacher.skypeId,
      });
    }
  }, [getCurrentProfileTeacher, loading, profileTeacher]);

  return (
    <>
      {loading || uploading ? (
        <BackdropSpinner />
      ) : (
        <Formik
          enableReinitialize
          initialValues={formData || initialValues}
          validationSchema={validationSchema}
          onSubmit={async (values, { setSubmitting }) => {
            const {
              teacherAvatar,
              selectedTeacherAvatar,
              expImages,
              selectedExpImages,
              degreeImages,
              selectedDegreeImages,
              dateOfBirth,
              typeOfTeacher,
              hometown,
              skypeId,
              video,
              selectedVideo,
              introduction,
              phoneNumber,
              nameOnCard,
              cardNumber,
              bankName,
              thumbnail,
            } = values;

            createOrUpdateProfileTeacher(
              {
                teacherAvatar,
                selectedTeacherAvatar,
                expImages,
                selectedExpImages,
                degreeImages,
                selectedDegreeImages,
                video,
                selectedVideo,
                dateOfBirth,
                typeOfTeacher,
                hometown,
                skypeId,
                introduction,
                phoneNumber,
                nameOnCard,
                cardNumber,
                bankName,
                thumbnail,
              },
              history,
              profileTeacher ? true : false
            );
          }}
        >
          {({ values, isSubmitting, isValid, errors }) => (
            <Form autoComplete="off">
              <Grid
                container
                className="container"
                direction="column"
                justifyContent="center"
              >
                <Grid item container alignItems="center">
                  <Grid item>
                    <Link to="/teachers/dashboard">
                      <ArrowBackIcon fontSize="large" color="primary" />
                    </Link>
                  </Grid>
                  <Grid item>
                    <Typography
                      variant="body2"
                      component={Link}
                      to="/teachers/dashboard"
                      style={{
                        fontWeight: 600,
                        marginLeft: "0.5em",
                        textDecoration: "none",
                      }}
                      className={classes.linkText}
                    >
                      Back
                    </Typography>
                  </Grid>
                </Grid>

                <Grid item>
                  {matchesMD && (
                    <Stepper
                      activeStep={activeStep}
                      alternativeLabel
                      style={{ backgroundColor: "transparent" }}
                    >
                      {steps.map((label) => (
                        <Step key={label}>
                          <StepLabel>{label}</StepLabel>
                        </Step>
                      ))}
                    </Stepper>
                  )}
                </Grid>

                {pages[page]}

                <Grid
                  item
                  container
                  justifyContent="center"
                  alignItems="center"
                  spacing={3}
                  style={{ marginTop: "1em", width: "100%", margin: 0 }}
                >
                  {page !== 0 && (
                    <Grid item>
                      <Button
                        onClick={() => prevPage()}
                        color="primary"
                        variant="contained"
                        disableRipple
                        style={{ color: "white" }}
                      >
                        <Grid
                          container
                          justifyContent="center"
                          alignItems="center"
                        >
                          <Grid item>
                            <ArrowBackIcon style={{ fontSize: "1.5rem" }} />
                          </Grid>

                          <Grid item>
                            <Typography
                              variant="body1"
                              style={{ fontWeight: 500 }}
                            >
                              &nbsp;Back
                            </Typography>
                          </Grid>
                        </Grid>
                      </Button>
                    </Grid>
                  )}
                  {page === pages.length - 1 ? (
                    <Grid item>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disableRipple
                        disabled={
                          !isValid || isSubmitting || isButtonDisabled(values)
                        }
                        style={{ color: "white" }}
                      >
                        <Grid
                          container
                          justifyContent="center"
                          alignItems="center"
                        >
                          <Grid item>
                            <SaveIcon style={{ fontSize: "1.5rem" }} />
                          </Grid>

                          <Grid item>
                            <Typography
                              variant="body1"
                              style={{ fontWeight: 500 }}
                            >
                              &nbsp;Save
                            </Typography>
                          </Grid>
                        </Grid>
                      </Button>
                    </Grid>
                  ) : page !== 0 ? (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        nextPage();
                      }}
                      disableRipple
                      style={{ color: "white" }}
                    >
                      Next
                    </Button>
                  ) : null}
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  profileTeacher: state.profileTeacher,
});

export default connect(mapStateToProps, {
  getCurrentProfileTeacher,
  createOrUpdateProfileTeacher,
})(ProfileTeacher);
